import { useCallback, useContext, useEffect, useState } from "react";

import { MinusCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useLazyQuery } from "@apollo/client";
import { Form, Input, Select } from "antd";
import { clsx } from "clsx";
import isEmpty from "lodash/isEmpty";
import { useDispatch } from "react-redux";

import { RVFormFloatingLabel, RVButton, getColor, RVFormItem, RVColumn, RVFormToggle } from "@regal-voice/ui";
import { JourneyFormContext } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/Contexts/JourneyFormContext";
import { getEventType } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/helpers";
import { getEventFields, getEventNames } from "ServerData/EventSchema/queries";
import { setFields } from "Services/state/journeys/JourneyStateSlice";
import { ConditionalEventType, NodeTriggerType } from "Types/Journey";

import { useQueryContactAttributes } from "../../../../../shared/hooks/useQueryContactAttributes";
import { unflattenFields } from "../../Utils/utils";
import { REGAL_VOICE_DEFAULT_EVENTS } from "../Shared/FormItemInputs/constants";
import { HighlightSelect } from "../Shared/FormItemInputs/HighlightSelect";
import { NodeNameFormItem } from "../Shared/FormItemInputs/NodeNameFormItem";
import { ConditionalNodeEventComparator } from "./components/ConditionalNodeEventComparator";
import { PropertyFilterCriterion } from "./PropertyFilterCriterion";
import { PropertyFilterCriterionProps, PropertyItemCriterion } from "./types";

import type { EventSchemaOptionEntry } from "ServerData/EventSchema/eventSchemaTypes";

import styles from "./ConditionalNodeItem.module.scss";

export function ConditionalNodeItem({
    formValues = {},
    triggeringEvent,
}: {
    formValues: Record<string, any>;
    triggeringEvent: { eventType?: string; eventName?: string };
}): JSX.Element {
    const { conditionalType, eventName, attributeName, eventOperator, filters } = formValues;
    // @todo - figure out why this needs to be asserted
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { formSubmited } = useContext(JourneyFormContext)!;
    const [eventNames, setEventNames] = useState([] as string[]);
    const [eventSchema, setEventSchema] = useState<EventSchemaOptionEntry>({});
    // @TODO replace with useContactAttributesFullListQuery, no need for the useEffect
    // @see src/ServerData/EventSchema/Hooks/useContactAttributesQuery/useContactAttributesQuery.ts
    /* Getting the contact fields */
    const { loadContactAttributes, contactAttributes } = useQueryContactAttributes({});
    const dispatch = useDispatch();
    const isEventCondtional = [ConditionalEventType.Custom, ConditionalEventType.RegalVoice].includes(conditionalType);
    const isNotTriggeringNodeType = [NodeTriggerType.AddContactToStaticSegment, NodeTriggerType.Scheduled].includes(
        triggeringEvent?.eventType as NodeTriggerType
    );

    const isContactCondtional = conditionalType === ConditionalEventType.ContactAttribute;
    const isTriggeringEventConditional = conditionalType === ConditionalEventType.TriggeringEvent;

    function getFilterCommonProps(): Partial<PropertyFilterCriterionProps> {
        return {
            formValues,
            isTriggeringEventConditional,
            isEventCondtional,
            isContactCondtional,
            parentConditionValues: {
                conditionalType,
                attributeName,
                eventName,
            },
            autocompleteSchemas: {
                contactAttributes,
                eventSchema,
            },
        };
    }

    function getEventFieldQueryVariables() {
        let eventTypeVariable = conditionalType;
        let eventNameVariable = eventName;

        if (conditionalType === ConditionalEventType.TriggeringEvent && !!triggeringEvent) {
            // @todo - figure out why this needs to be asserted
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            eventTypeVariable = triggeringEvent.eventType!;
            // @todo - figure out why this needs to be asserted
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            eventNameVariable = triggeringEvent.eventName!;
        }

        eventTypeVariable = getEventType(eventTypeVariable);

        return { eventName: eventNameVariable, eventType: eventTypeVariable };
    }

    const [loadEventNames] = useLazyQuery(getEventNames, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "no-cache",
        variables: {
            eventType: conditionalType === ConditionalEventType.Custom ? "track" : "regal_voice_event",
        },
        onCompleted(data) {
            const eventNames = [...data.getEventNames.items].sort((a, b) =>
                a.toLowerCase() > b.toLowerCase() ? 1 : b.toLowerCase() > a.toLowerCase() ? -1 : 0
            );
            setEventNames(eventNames);
            setEventSchema({});
            if (eventName?.length > 0) {
                loadEventFields({
                    variables: getEventFieldQueryVariables(),
                });
            }
        },
    });

    const [loadEventFields] = useLazyQuery(getEventFields, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "no-cache",
        variables: getEventFieldQueryVariables(),
        onCompleted(data) {
            const schemaOptions = {} as Record<string, any>;

            for (const item of data.getEventSchema as Record<string, string>[]) {
                schemaOptions[item.fieldName] = {
                    fieldType: item.fieldType,
                    fieldId: item.fieldId,
                };
            }
            setEventSchema(schemaOptions);
        },
    });

    useEffect(() => {
        const { eventName: eventNameVariable, eventType } = getEventFieldQueryVariables();
        if (!!eventNameVariable?.trim()) {
            loadEventFields({
                variables: {
                    eventType,
                    eventName: eventNameVariable,
                },
            });
        } else {
            setEventSchema({});
        }
    }, [eventName, conditionalType]);

    useEffect(() => {
        // Dont have to load event names if the condition is based on triggering event (we DO still
        // have to load the event fields)
        if (![ConditionalEventType.TriggeringEvent, ConditionalEventType.RegalVoice].includes(conditionalType)) {
            loadEventNames({
                variables: {
                    eventType: conditionalType === ConditionalEventType.Custom ? "track" : "regal_voice_event",
                },
            });
        } else if (conditionalType === ConditionalEventType.RegalVoice) {
            setEventNames(REGAL_VOICE_DEFAULT_EVENTS);
        }
    }, [conditionalType]);

    useEffect(() => {
        if (isContactCondtional) {
            loadContactAttributes();
        }
    }, [isContactCondtional]);

    function onChangeConditionalType(conditionalType: ConditionalEventType) {
        const isEventCondtional = [ConditionalEventType.Custom, ConditionalEventType.RegalVoice].includes(
            conditionalType
        );
        dispatch(
            setFields(
                unflattenFields({
                    conditionalType,
                    eventName: undefined,
                    eventOperator: "isSet",
                    lastEventOnly: undefined,
                    ...(isEventCondtional ? { filters: [] } : { filters: [{ propertyOperator: "isSet" }] }),
                })
            )
        );
    }

    function onChangeEventName(value: string) {
        dispatch(
            setFields(
                unflattenFields({
                    ...formValues,
                    eventName: value,
                    eventOperator: "isSet",
                    filters: [],
                    lastEventOnly: undefined,
                })
            )
        );
    }

    const validateEventName = useCallback(
        async (_: unknown, eventName: any) => {
            if (!formSubmited) {
                return Promise.resolve();
            }
            if (isEventCondtional && !eventName) {
                return Promise.reject("Event name is required");
            }
            return Promise.resolve();
        },
        [formSubmited, isEventCondtional]
    );

    const propertyFiltersCriteriaValidator = useCallback(
        async (_: unknown, propertyItems: PropertyItemCriterion[]) => {
            if (!formSubmited || (eventOperator == "isNotSet" && propertyItems.length == 0)) {
                return Promise.resolve();
            }
            if (eventOperator == "isNotSet" && propertyItems.length > 0) {
                return Promise.reject(new Error("Not exists operator does not support property filters"));
            }
            return Promise.resolve();
        },
        [formSubmited, eventOperator]
    );

    const removeFilter = (index: number) => {
        const filtersCopy = [...filters];
        filtersCopy.splice(index, 1);
        dispatch(
            setFields(
                unflattenFields({
                    ...formValues,
                    filters: filtersCopy,
                })
            )
        );
    };
    const addFilter = (newObjState: any) => {
        const filtersCopy = [...filters];
        filtersCopy.push(newObjState);
        dispatch(
            setFields(
                unflattenFields({
                    ...formValues,
                    filters: filtersCopy,
                })
            )
        );
    };

    return (
        <>
            <RVFormItem noStyle name={"complexConditional"} key={`complexConditional`} initialValue={true}>
                <Input type={"hidden"} />
            </RVFormItem>
            <NodeNameFormItem title="Condition Name" />
            <RVFormItem label="Contacts who meet these criteria:">
                <RVColumn gap={2} className={clsx(styles.conditionFiltersContainer, "floating-design")}>
                    <RVFormFloatingLabel name="conditionalType">
                        <RVFormItem
                            name="conditionalType"
                            className={clsx(styles.formItem, {
                                [styles.noMargin]: !conditionalType,
                            })}
                            rules={[
                                {
                                    required: true,
                                    message: "Select condition",
                                },
                            ]}
                            label={"Condition Type"}
                        >
                            <Select onChange={onChangeConditionalType} data-testid="conditionalType-dropdown">
                                <Select.Option value={ConditionalEventType.ContactAttribute}>
                                    Contact Attribute
                                </Select.Option>
                                <Select.Option value={ConditionalEventType.Custom}>Custom Event</Select.Option>
                                <Select.Option value={ConditionalEventType.RegalVoice}>Regal Voice Event</Select.Option>
                                {!isNotTriggeringNodeType && (
                                    <Select.Option value={ConditionalEventType.TriggeringEvent}>
                                        Triggering Event
                                    </Select.Option>
                                )}
                            </Select>
                        </RVFormItem>
                    </RVFormFloatingLabel>

                    {!!conditionalType && (
                        <>
                            {isEventCondtional && (
                                <>
                                    <RVFormFloatingLabel name="eventName">
                                        <RVFormItem
                                            name="eventName"
                                            className={styles.formItem}
                                            rules={[
                                                {
                                                    validator: validateEventName,
                                                    message: "Event name is required",
                                                },
                                            ]}
                                            label={"Event Name"}
                                        >
                                            <HighlightSelect
                                                options={eventNames}
                                                onChange={onChangeEventName}
                                                data-testid={`select-eventName`}
                                            />
                                        </RVFormItem>
                                    </RVFormFloatingLabel>
                                    <ConditionalNodeEventComparator
                                        formValues={formValues}
                                        isEventCondtional={isEventCondtional}
                                    />
                                </>
                            )}
                            <Form.List
                                name="filters"
                                rules={[
                                    {
                                        validator: propertyFiltersCriteriaValidator,
                                    },
                                ]}
                                initialValue={[]}
                            >
                                {(fields, {}, { errors }) => {
                                    const triggeringEventHasLessThan2Filters =
                                        isTriggeringEventConditional && fields.length < 2;
                                    return (
                                        <>
                                            {fields.length > 0 && eventOperator != "isNotSet" && (
                                                <div
                                                    key={`conditionDivider-where-clause-filters`}
                                                    className={styles.conditionDivider}
                                                >
                                                    <span className={styles.text}>WHERE</span>
                                                    {!(isContactCondtional || triggeringEventHasLessThan2Filters) && (
                                                        <span>
                                                            <MinusCircleOutlined
                                                                style={{
                                                                    color: getColor("red4"),
                                                                    cursor: "pointer",
                                                                }}
                                                                onClick={() => removeFilter(fields[0].name)}
                                                            />
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            {fields
                                                .map((rootField) => (
                                                    <RVFormItem
                                                        key={`floating-form-item-container-${rootField.key}`}
                                                        noStyle
                                                    >
                                                        <PropertyFilterCriterion
                                                            {...(getFilterCommonProps() as any)}
                                                            filterItem={formValues?.filters?.[rootField.name] || {}}
                                                            rootField={rootField}
                                                            triggeringEvent={triggeringEvent}
                                                        />
                                                    </RVFormItem>
                                                ))
                                                ?.reduce(
                                                    (prev, curr, index) => {
                                                        if (!filters?.[index]) {
                                                            return <></>;
                                                        }
                                                        return (
                                                            <>
                                                                {prev}
                                                                {index > 0 && (
                                                                    <div
                                                                        key={`conditionDivider-${index}`}
                                                                        className={styles.conditionDivider}
                                                                    >
                                                                        <span className={styles.text}>AND</span>
                                                                        <span>
                                                                            <MinusCircleOutlined
                                                                                style={{
                                                                                    color: getColor("red4"),
                                                                                    cursor: "pointer",
                                                                                }}
                                                                                onClick={() => removeFilter(index)}
                                                                            />
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {curr}
                                                            </>
                                                        );
                                                    },
                                                    <></>
                                                )}
                                            <div
                                                className={clsx({
                                                    [styles.errorStyle]: !isEmpty(errors),
                                                })}
                                            >
                                                <Form.ErrorList errors={errors} />
                                            </div>

                                            {!isContactCondtional && ["isSet", undefined].includes(eventOperator) && (
                                                <RVButton
                                                    icon={<PlusCircleOutlined />}
                                                    size="middle"
                                                    type="link"
                                                    onClick={() => addFilter({ propertyOperator: "isSet" })}
                                                    className={styles.addPropertyButton}
                                                >
                                                    Add Property Condition
                                                </RVButton>
                                            )}
                                        </>
                                    );
                                }}
                            </Form.List>
                            {isEventCondtional && eventOperator != "isNotSet" && filters && filters.length > 0 && (
                                <RVFormToggle
                                    label="Last Event Only"
                                    tooltip="If contact has more than one instance of this event, only reference the last instance (not all) to satisfy condition"
                                    name="lastEventOnly"
                                />
                            )}
                        </>
                    )}
                </RVColumn>
            </RVFormItem>
        </>
    );
}
